import _ from 'lodash';
import {
  type PortalSettings,
  type ClientSettings,
  type ClientSettingsUpdate,
  type AccountCreate,
  type AccountUpdate,
  type AccountImport,
  type TriggerCommentAutomation,
} from '@gorilla/portal-shared/src/schemas';
import { User } from '@gorilla/common/src/lib/monday-api/api';
import { getSessionToken, getContext } from './monday';

export async function handleFetchErrorResponse(fallbackMessage: string, res: Response) {
  if (!res.ok) {
    let result = null;

    try {
      result = await res.clone().json();
    } catch (err) {
      result = await res.clone().text();
    }

    let error = new Error(fallbackMessage);

    if (_.isString(_.get(result, 'error'))) {
      // TODO: also handle zod errors
      error = new Error(_.get(result, 'error'));
    }

    if (_.get(result, 'error.name') === 'ZodError') {
      error = new Error(`${fallbackMessage} (Invalid data)`);
    }

    if (_.isString(_.get(result, 'message'))) {
      error = new Error(result.message);
    }

    if (_.isString(result)) {
      error = new Error(result);
    }

    if (_.get(result, 'code')) {
      // @ts-ignore
      error.code = result.code;
    }

    throw error;
  }
}

// @ts-ignore
const apiUrl = import.meta.env.VITE_APP_API_URL;

export type Portal = {
  id: string;
  lastUpdatedAt: number | null;
  lastPublishedAt: number | null;
  portalManager: User | null;
  portalManagerPermissions: string[] | null;
  extensionId?: string;
  settings: PortalSettings;
};

export type Slot = {
  slotId: string;
  label: string;
  description?: string;
  templateBoardId?: number;
};

export type Client = {
  settings: ClientSettings;
  slots: Slot[];
};

export const REGION_NAMES = {
  us: 'the United States',
  eu: 'Europe',
  au: 'Australia',
  il: 'Israel',
} as const;

async function getRegion() {
  const context = await getContext();
  const region = context.region;
  const regionShort = region ? region.substring(0, 2) : 'us';

  if (['us', 'eu', 'il'].includes(regionShort)) {
    return regionShort;
  }

  return 'us';
}

export async function getBackendUrl() {
  const backendUrl = import.meta.env.VITE_APP_BACKEND_URL;
  const region = await getRegion();

  if (backendUrl.includes('-REGION-')) {
    return backendUrl.replace('-REGION-', region);
  }

  return backendUrl;
}

async function getAuthHeaders() {
  const sessionToken = await getSessionToken();

  return {
    Authorization: `${sessionToken}`,
  };
}

export async function getPortal(boardId: number, namespace = 'draft') {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/board/${boardId}/${namespace}`, {
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to get portal', response);

  return (await response.json()) as Portal;
}

export async function saveSettings(boardId: number, settings: PortalSettings) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/board/${boardId}/draft`, {
    method: 'PUT',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
    body: JSON.stringify(settings),
  });

  if (response.status === 404) {
    return null;
  }

  await handleFetchErrorResponse('Failed to save settings', response);

  return await response.json();
}

export async function publishPortal(boardId: number) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/board/${boardId}/publish`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to publish portal', response);

  return await response.json();
}

export async function unpublishPortal(boardId: number) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/board/${boardId}/unpublish`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to unpublish portal', response);

  return await response.json();
}

export async function discardDraft(boardId: number) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/board/${boardId}/discard-draft`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to discard draft', response);

  return await response.json();
}

export async function createActivity(portalId: string, clientItemId: number, message: string, itemId: number) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/activity`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      portalId,
      clientItemId,
      itemId,
      message,
    }),
  });

  if (response.status === 404) {
    return null;
  }

  await handleFetchErrorResponse('Failed to create activity', response);

  return await response.json();
}

export async function createPreviewToken(boardId: number, clientItemId: number, scope: 'draft' | 'public') {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/board/${boardId}/preview-token`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      clientItemId,
      scope,
    }),
  });

  await handleFetchErrorResponse('Failed to create preview token', response);

  return await response.json();
}

export async function uploadFile(boardId: number, uploadSettings: { name: string; content: string }) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/board/${boardId}/file`, {
    method: 'PUT',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
    body: JSON.stringify(uploadSettings),
  });

  await handleFetchErrorResponse('Failed to upload file', response);

  return (await response.json()) as { hash: string };
}

export async function getFile(boardId: number, fileHash: string) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/board/${boardId}/file/${fileHash}`, {
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to get file', response);

  return (await response.json()) as { name: string; content: string };
}

export async function getClient(boardId: number, itemId: number) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/client/${boardId}/${itemId}`, {
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to get client', response);

  return (await response.json()) as Client;
}

export async function searchClients(boardId: number, cursor?: string) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/board/${boardId}/clients_search${cursor ? `/${cursor}` : ''}`, {
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to get clients', response);

  return (await response.json()) as { results: Client[]; cursor: string | null };
}

export async function getMe() {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/me`, {
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to get user info', response);

  return (await response.json()) as { hasValidOauthToken: boolean };
}

// The authorize URL is built by the backend, not here: OAuth 2.1 requires a PKCE code_verifier that
// has to survive until the backend exchanges the code, and it must never reach the browser.
export async function getAuthorizeUrl(boardId: number, slug?: string) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/oauth/authorize-url`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
    body: JSON.stringify({ boardId, slug }),
  });

  await handleFetchErrorResponse('Failed to start the authorization', response);

  return (await response.json()) as { url: string };
}

export async function updateClientSettings(boardId: number, itemId: number, clientSettingsUpdate: ClientSettingsUpdate) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/client/${boardId}/${itemId}`, {
    method: 'PUT',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
    body: JSON.stringify(clientSettingsUpdate),
  });

  await handleFetchErrorResponse('Failed to save client settings', response);

  return await response.json();
}

export async function addAccount(boardId: number, itemId: number, account: AccountCreate, overwriteExisting = false) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/client/${boardId}/${itemId}/account${overwriteExisting ? '?overwriteExisting=yes' : ''}`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
    body: JSON.stringify(account),
  });

  await handleFetchErrorResponse('Failed to add account', response);

  return await response.json();
}

export async function importAccount(boardId: number, itemId: number, account: AccountImport) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/client/${boardId}/${itemId}/import-account`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
    body: JSON.stringify(account),
  });

  await handleFetchErrorResponse('Failed to add account', response);

  return await response.json();
}

export async function exportAccount(boardId: number, itemId: number, accountId: string) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/client/${boardId}/${itemId}/account/${accountId}/export`, {
    method: 'GET',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to export account', response);

  return await response.json();
}

export async function updateAccount(boardId: number, itemId: number, accountId: string, accountUpdate: AccountUpdate) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/client/${boardId}/${itemId}/account/${accountId}`, {
    method: 'PUT',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
    body: JSON.stringify(accountUpdate),
  });

  await handleFetchErrorResponse('Failed to update account', response);

  return await response.json();
}

export async function deleteAccount(boardId: number, itemId: number, accountId: string) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/client/${boardId}/${itemId}/account/${accountId}`, {
    method: 'DELETE',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to delete account', response);

  return await response.json();
}

export async function triggerCommentAutomation(params: TriggerCommentAutomation) {
  const authHeaders = await getAuthHeaders();
  const backendUrl = await getBackendUrl();

  const response = await fetch(`${backendUrl}/trigger-comment-automation`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  await handleFetchErrorResponse('Failed to trigger comment automation', response);

  return await response.json();
}

export type RegistryEntry = {
  account_id: string;
  portal_id: string;
  kind: string;
  domain: string;
  cloudflare_hostname_id: string;
};

export type PortalShort = {
  portalId: string;
  type: 'success';
  portalName: string | null;
  boardId: string | null;
  lastUpdatedAt: number | null;
  lastPublishedAt: number | null;
  customDomain: string | null;
  subdomain: string | null;
};

export async function getRegistryEntries(boardId: number) {
  const authHeaders = await getAuthHeaders();
  const region = await getRegion();

  const response = await fetch(`${apiUrl}/registry/portal/${boardId}?region=${region}`, {
    method: 'GET',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to get registry entries', response);

  return (await response.json()) as RegistryEntry[];
}

export async function getPortalsByAccount() {
  const authHeaders = await getAuthHeaders();
  const region = await getRegion();

  const response = await fetch(`${apiUrl}/registry/account-portals?region=${region}`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to get portals for account', response);
  const { portals } = (await response.json()) as { portals: PortalShort[] };

  return portals;
}

export async function connectCustomDomain(boardId: number, customDomain: string) {
  const authHeaders = await getAuthHeaders();
  const region = await getRegion();

  const response = await fetch(`${apiUrl}/registry/connect-custom/${boardId}?region=${region}`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      domain: customDomain,
    }),
  });

  await handleFetchErrorResponse('Failed to connect custom domain', response);

  return await response.json();
}

export async function disconnectCustomDomain(boardId: number) {
  const authHeaders = await getAuthHeaders();
  const region = await getRegion();

  const response = await fetch(`${apiUrl}/registry/disconnect-custom/${boardId}?region=${region}`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to disconnect custom domain', response);

  return await response.json();
}

// Deletes a portal's Cloudflare custom hostname + D1 registry/portals rows (admin-only).
export async function deletePortalByPortalId(portalId: string) {
  const authHeaders = await getAuthHeaders();
  const region = await getRegion();

  const response = await fetch(`${apiUrl}/registry/delete-portal/${portalId}?region=${region}`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to delete portal', response);

  return await response.json();
}

// Deletes all of a portal's monday Storage + Secure-Storage data (admin-only, idempotent).
export async function deletePortalStorage(boardId: number) {
  const authHeaders = await getAuthHeaders();
  const region = await getRegion();

  const response = await fetch(`${apiUrl}/registry/delete-portal-storage/${boardId}?region=${region}`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to delete portal storage', response);

  return await response.json();
}

export async function verifyCustomDomain(boardId: number) {
  const authHeaders = await getAuthHeaders();
  const region = await getRegion();

  const response = await fetch(`${apiUrl}/registry/verify-custom-domain/${boardId}?region=${region}`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to verify custom domain', response);

  return await response.json();
}

export async function updatePortalRegion(boardId: number) {
  const authHeaders = await getAuthHeaders();
  const region = await getRegion();

  const response = await fetch(`${apiUrl}/meta/portal/${boardId}?region=${region}`, {
    method: 'PUT',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      region,
    }),
  });

  await handleFetchErrorResponse('Failed to update portal region', response);

  return await response.json();
}

function getUserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error('Could not determine time zone:', error);
    return 'America/New_York'; // Default fallback
  }
}

export type LoginEvent = { day: string; logins_count: number };
export type RequestEvent = { day: string; requests_count: number };
export type ActivityEvent = {
  sample_interval: string;
  path: string;
  client_item_id: number;
  account_hash: string;
  portal_id: string;
  timestamp: string;
  is_credentials_auth: 1 | 0;
  is_google_auth: 1 | 0;
  is_comment: 1 | 0;
};

export type AnalyticsData = {
  recentActivityLog: ActivityEvent[];
  loginsPerDay: LoginEvent[];
  successRequestsPerDay: RequestEvent[];
  errorRequestsPerDay: RequestEvent[];
  healthcheckLoginsPerDay: LoginEvent[];
};

export async function getPortalAnalytics(boardId: number) {
  const authHeaders = await getAuthHeaders();
  const region = await getRegion();
  const timeZone = getUserTimeZone();

  const response = await fetch(`${apiUrl}/analytics/stats/${boardId}?region=${region}&timezone=${timeZone}`, {
    method: 'GET',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
  });

  await handleFetchErrorResponse('Failed to get analytics data', response);

  return (await response.json()) as AnalyticsData;
}
